import type { CSSProperties, ElementType, ReactNode } from 'react';
import { Fragment, createElement, useMemo } from 'react';

import type { SvgIconsList } from '@snowit/assets';
import type { Alignment, Classnames, Color, TypoColor, TypoSize } from '@snowit/icetypes';
import type { Components, ExtraProps } from 'react-markdown';
import type { MarkdownTag } from './constants';

import { TYPO_SIZE_SCALE } from '@snowit/iceconst';
import { COLOR } from '@snowit/icetypes';

import Icon from '../../Icon';
import { MARKDOWN_TAGS } from './constants';

import cx from 'classnames';
import s from './Markdown.module.scss';

import ReactMarkdown from 'react-markdown';

export type { MarkdownTag };

export { MARKDOWN_TAGS };

// replaces react-markdown v8's ReactMarkdownProps, removed in v9
export type MarkdownRenderProps = ExtraProps & {
  children?: ReactNode;
  className?: string;
  href?: string;
  src?: string;
  alt?: string;
};

export type MarkdownRenderers = Partial<
  Record<MarkdownTag, (props: MarkdownRenderProps) => ReactNode>
>;

export type MarkdownClasses = Classnames<'text' | MarkdownTag>;
export type MarkdownLinkTarget = '_blank' | '_self';
export type MarkdownOpts = {
  linkTarget?: MarkdownLinkTarget;
  customDot?: { name?: SvgIconsList; label?: string };
  styles?: CSSProperties;
};

export interface IMarkdown {
  source: string;
  baseSize?: TypoSize | [TypoSize, TypoSize];
  sizeIncrease?: number;
  color?: TypoColor;
  customDot?: { name?: SvgIconsList; label?: string };
  align?: Alignment;
  balancer?: boolean;
  wrap?: boolean;
  classNames?: MarkdownClasses;
  linkTarget?: MarkdownLinkTarget;
  renderers?: MarkdownRenderers;
  /** element wrapping each markdown chunk; pass false for non-DOM targets (e.g. react-pdf) */
  wrapper?: ElementType | false;
}

export type MarkdownRenderer = {
  classNames: MarkdownClasses;
  opts: MarkdownOpts;
};

const renderTag = (
  tag: MarkdownTag,
  params: MarkdownRenderer,
): ((props: MarkdownRenderProps) => ReactNode) => {
  const { classNames, opts } = params;
  switch (tag) {
    case 'a':
      return ({ node: _node, ...props }: MarkdownRenderProps) => (
        <a
          target={opts.linkTarget}
          rel={'noreferrer'}
          className={cx(s.a, classNames.a)}
          style={opts.styles}
          {...props}
        />
      );
    case 'li':
      return (props: MarkdownRenderProps) => {
        if (opts.customDot) {
          return (
            <li className={cx(s.liCustom, classNames.liCustom)} style={opts.styles}>
              <Icon
                className={s.customDot}
                name={opts.customDot.name}
                a11y={{ label: opts.customDot.label, hidden: !opts.customDot.label }}
              />
              <div>{props.children}</div>
            </li>
          );
        }
        return (
          <li className={cx(s.li, classNames.li)} style={opts.styles}>
            {props.children}
          </li>
        );
      };
    case 'ul':
      return ({ node: _node, children, ...rest }: MarkdownRenderProps) => (
        <ul style={opts.styles} className={cx(s.ul, classNames.ul)} {...rest}>
          {children}
        </ul>
      );
    default:
      return ({ node: _node, ...props }: MarkdownRenderProps) =>
        createElement(tag, {
          style: opts.styles,
          className: cx(s[tag], classNames[tag]),
          ...props,
        });
  }
};

// 'liCustom' is a styling key, not an HTML tag — it is handled inside the 'li' renderer
const COMPONENT_TAGS = MARKDOWN_TAGS.filter((tag) => tag !== 'liCustom');

const renderers = (params: MarkdownRenderer): Components =>
  COMPONENT_TAGS.reduce<Record<string, (props: MarkdownRenderProps) => ReactNode>>((memo, tag) => {
    memo[tag] = renderTag(tag, params);
    return memo;
  }, {}) as Components;

const Markdown = (props: IMarkdown) => {
  const {
    source: rawSource,
    baseSize = 'df',
    sizeIncrease = 0.35,
    color: _color = 'text',
    customDot,
    align,
    balancer,
    wrap,
    classNames,
    linkTarget = '_blank',
    wrapper = 'div',
  } = props;

  const [baseSizeMin, baseSizeMax] = Array.isArray(baseSize) ? baseSize : [baseSize, baseSize];
  const parsed: Record<string, string>[] = [];
  let m: RegExpExecArray | null = null;
  const source = (rawSource || '')
    .replace(/[“”]/g, '"')
    .replace(/\\"/g, '"')
    .replace(/\\?\\"/g, '"')
    .replace(/\\?!/g, '!');

  // define the regex rules to parse the markdown source
  const regex = new RegExp(/<!--REACT\s(.*?)\sREACT-->/g);
  const split = new RegExp(/<!--REACT\s.*?\sREACT-->/g);
  do {
    m = regex.exec(source);
    if (m?.[1]) {
      const rgx = /(?<component>\w+)\s?("(?<props>.*)")?/;
      const mm = m[1].match(rgx);
      if (mm?.groups) {
        parsed.push(mm.groups);
      }
    }
  } while (m);

  const splitMd = source.split(split);

  const components = {};

  const color = useMemo(() => {
    return COLOR.includes(_color as Color) ? `var(--c-${_color})` : _color;
  }, [_color]);

  const RenderComponent = ({ index }: { index: number }) => {
    const key = parsed[index];
    if (!components) return null;
    if (!key?.component) return null;
    const Component = components[key.component];
    if (!Component) return null;
    const params = JSON.parse(key?.props || '');
    return <Component {...params} />;
  };

  if (!source) return '';

  const Wrapper = wrapper === false ? Fragment : wrapper;

  return splitMd.filter(Boolean).map((md, i) => {
    const unescaped = md
      .replace(/\\\\n/g, '\n')
      .replace(/\\#/g, '#')
      .replace(/\\n/g, '\n')
      // .replace(/\n/, '\n\n')
      .replace(/\n/, '\n');
    // const unescaped = md.replace(/\\\\n/g, '\n');

    const styles = {
      // '--lfs': `var(--fs-${baseSize})`,
      '--lfs-min': `var(--fs-${baseSizeMin})`,
      '--lfs-max': `var(--fs-${baseSizeMax})`,
      '--lfs-increase': `${sizeIncrease}vw`,
      '--lfs-relative': `${TYPO_SIZE_SCALE[baseSizeMin] / TYPO_SIZE_SCALE[baseSizeMax]}rem`,
      '--lc': color,
      '--lalign': align,
    } as CSSProperties;

    return (
      <Fragment key={i}>
        {/* v10 dropped the className prop: the wrapper the styles target is now explicit */}
        <Wrapper
          {...(wrapper === false
            ? {}
            : {
                className: cx(s.text, classNames?.text, {
                  markdownWrapClass: wrap,
                  [s.balancer]: balancer,
                }),
              })}
        >
          <ReactMarkdown
            components={
              (props.renderers as Components) ||
              renderers({
                classNames: classNames ?? {},
                opts: {
                  styles,
                  customDot,
                  linkTarget,
                },
              })
            }
          >
            {unescaped}
          </ReactMarkdown>
        </Wrapper>
        <RenderComponent index={i} />
      </Fragment>
    );
  });
};

export default Markdown;
